/**
 * 画布上的编辑动作 → 模板改动。
 *
 * 画布跑在 iframe 里，只能 postMessage 出坐标和动作名；真正改模板在这里。
 * 抽成纯函数是为了两个编辑器（侧边栏的 EditorPage、独立标签页的
 * FullscreenOverlay）共用同一套语义 —— 三样画布能力（斜杠插块、分栏、
 * 表格右键）如果各写各的，很快就会做出三套互相打架的行为。
 */
import {
  insertCol,
  insertRow,
  mergeRange,
  patchCells,
  removeCol,
  removeRow,
  setColWidth,
  setRowHeight,
  splitCell,
} from './freeTable';
import type { CellProps, Rect } from './freeTable';
import { createDefaultSection } from '../components/ContractMode/SectionEditor';
import { genId } from './types';
import { extractWholeWrapStyles } from '../../render-core/renderer';
import type { ColumnsSection, ContractSection, ContractSectionType, FreeTableSection } from './types';
import {
  findSectionDeep,
  insertAfterDeep,
  locateSection,
  mapSectionsDeep,
} from '../../render-core/sectionTree';

export interface TableOpMessage {
  op:
    | 'merge'
    | 'split'
    | 'insertRow'
    | 'removeRow'
    | 'insertCol'
    | 'removeCol'
    | 'evenCols'
    | 'colResize'
    | 'rowHeight'
    | 'rowHeightAll';
  id: string;
  r0: number;
  c0: number;
  r1: number;
  c1: number;
  /** 插入/删除行列时的位置。 */
  at?: number;
  /** colResize = 新列宽 %；rowHeight = 新行高 mm（null 恢复自适应）。 */
  value?: number | null;
}

/**
 * 拖列线只动相邻两列：左边宽一点右边就窄一点，总宽不变。
 * setColWidth 是把余量按比例摊给所有列，用在属性栏填数字合适，用在拖拽上
 * 会让离得老远的列也跟着抖。最后一列右边没有邻居，退回按比例。
 */
export function resizeColPair(t: FreeTableSection, c: number, percent: number): FreeTableSection {
  const n = t.colWidths.length;
  if (c < 0 || c >= n) return t;
  if (c === n - 1) return setColWidth(t, c, percent);
  const total = t.colWidths[c] + t.colWidths[c + 1];
  const MIN = 3;
  const left = Math.max(MIN, Math.min(total - MIN, percent));
  const widths = t.colWidths.map((w, i) => (i === c ? left : i === c + 1 ? total - left : w));
  return { ...t, colWidths: widths.map((w) => Math.round(w * 10) / 10) };
}

/** 对某个表格区块套用一次结构操作。找不到区块或不是表格时原样返回。 */
export function applyTableOp(
  sections: ContractSection[],
  msg: TableOpMessage,
): ContractSection[] {
  return mapSectionsDeep(sections, (sec) => {
    if (sec.id !== msg.id || sec.type !== 'free_table') return sec;
    const t = sec as FreeTableSection;
    const rect = { r0: msg.r0, c0: msg.c0, r1: msg.r1, c1: msg.c1 };
    switch (msg.op) {
      case 'merge':
        return mergeRange(t, rect);
      case 'split':
        return splitCell(t, msg.r0, msg.c0);
      case 'insertRow':
        return insertRow(t, msg.at ?? msg.r0);
      case 'removeRow':
        // 只剩一行就别删了，删完这张表在画布上彻底消失，用户找不回来
        return t.rows.length <= 1 ? t : removeRow(t, msg.at ?? msg.r0);
      case 'insertCol':
        return insertCol(t, msg.at ?? msg.c0);
      case 'removeCol':
        return t.colWidths.length <= 1 ? t : removeCol(t, msg.at ?? msg.c0);
      case 'colResize':
        return typeof msg.value === 'number' ? resizeColPair(t, msg.at ?? msg.c0, msg.value) : t;
      case 'rowHeight':
        return setRowHeight(t, msg.at ?? msg.r0, typeof msg.value === 'number' ? msg.value : null);
      case 'rowHeightAll': {
        // 把这一行的行高套到全表；value 缺省 = 取当前行的
        const from = t.rows[msg.at ?? msg.r0];
        const h = typeof msg.value === 'number' ? msg.value : from?.heightMm ?? null;
        return { ...t, rows: t.rows.map((r) => ({ ...r, heightMm: h })) };
      }
      case 'evenCols': {
        const n = t.colWidths.length;
        if (n === 0) return t;
        const w = Math.round((100 / n) * 10) / 10;
        let out = t;
        for (let i = 0; i < n; i++) out = setColWidth(out, i, w);
        return out;
      }
      default:
        return sec;
    }
  });
}

/** 批量改选区格子的样式（画布上选中格子后右侧属性栏改）。 */
export function applyCellPatch(
  sections: ContractSection[],
  id: string,
  rect: Rect,
  patch: Partial<CellProps>,
): ContractSection[] {
  return mapSectionsDeep(sections, (sec) => {
    if (sec.id !== id || sec.type !== 'free_table') return sec;
    return patchCells(sec as FreeTableSection, rect, patch);
  });
}

/** 往选中格子末尾追加文字（左栏点字段 → 落进格子）。 */
export function appendCellText(
  sections: ContractSection[],
  id: string,
  r: number,
  c: number,
  text: string,
): ContractSection[] {
  const sec = findSectionDeep(sections, id);
  if (!sec || sec.type !== 'free_table') return sections;
  const cell = (sec as FreeTableSection).rows[r]?.cells[c];
  if (!cell) return sections;
  const joiner = cell.content && !/\s$/.test(cell.content) ? ' ' : '';
  return applyCellText(sections, id, r, c, `${cell.content}${joiner}${text}`);
}

/** 深拷贝一个区块并给里面所有 id 换新 —— 复制区块时用，否则两个区块共用一套格子 id。 */
export function cloneWithNewIds<T>(value: T): T {
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
        out[k] = k === 'id' && typeof x === 'string' ? genId() : walk(x);
      }
      return out;
    }
    return v;
  };
  return walk(value) as T;
}

/** 复制一个区块，放在它自己后面。 */
export function duplicateSection(
  sections: ContractSection[],
  id: string,
): { sections: ContractSection[]; id: string | null } {
  const src = findSectionDeep(sections, id);
  if (!src) return { sections, id: null };
  const copy = cloneWithNewIds(src);
  return { sections: insertAfterDeep(sections, id, copy), id: copy.id };
}

/**
 * 把区块换成另一种类型，位置和 id 不变。
 * 标题 ↔ 文本段落这类都是「一段文字」的，正文原样带过去；其他类型内容
 * 结构完全不同，只能给一个该类型的空块。
 */
export function convertSection(
  sections: ContractSection[],
  id: string,
  type: string,
): ContractSection[] {
  const cur = findSectionDeep(sections, id);
  if (!cur || cur.type === type) return sections;
  const fresh = createBlock(type);
  if (!fresh) return sections;
  const textLike = (t: string) => t === 'header' || t === 'text_block';
  const carried =
    textLike(cur.type) && textLike(fresh.type)
      ? { ...fresh, content: (cur as { content?: string }).content ?? '' }
      : fresh;
  const replaced = { ...carried, id: cur.id } as ContractSection;
  return mapSectionsDeep(sections, (s) => (s.id === id ? replaced : s));
}

/**
 * 把某个区块里对字段 from 的引用换成 to。
 * 正文 / 格子里的 {{from}}（含 {{from|大写}} 这种带格式的）按文本替换；
 * 信息网格、签章行、附件、二维码这类按 fieldId 绑的，比对 id 或名字后换成新字段的 id。
 */
export function rebindField(
  sections: ContractSection[],
  id: string,
  from: string,
  to: string,
  fieldMetas: Array<{ id: string; name: string }>,
): ContractSection[] {
  const fromRaw = (from.includes('|') ? from.slice(0, from.lastIndexOf('|')) : from).trim();
  const toMeta = fieldMetas.find((f) => f.name === to || f.id === to);
  const toId = toMeta?.id ?? to;
  const toName = toMeta?.name ?? to;
  const fromMeta = fieldMetas.find((f) => f.name === fromRaw || f.id === fromRaw);
  const matchesFrom = (ref: string | null | undefined) =>
    !!ref && (ref === fromRaw || (fromMeta ? ref === fromMeta.id || ref === fromMeta.name : false));
  const esc = fromRaw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\{\\{\\s*${esc}\\s*(\\|[^}]*)?\\}\\}`, 'g');
  const swapText = (t: string) => t.replace(re, (_m, fmt: string | undefined) => `{{${toName}${fmt ?? ''}}}`);
  return mapSectionsDeep(sections, (sec) => {
    if (sec.id !== id) return sec;
    switch (sec.type) {
      case 'header':
      case 'text_block':
        return { ...sec, content: swapText(sec.content) };
      case 'free_table':
        return {
          ...sec,
          rows: sec.rows.map((row) => ({ ...row, cells: row.cells.map((c) => ({ ...c, content: swapText(c.content) })) })),
        };
      case 'info_grid':
        return { ...sec, items: sec.items.map((it) => (matchesFrom(it.fieldId) ? { ...it, fieldId: toId } : { ...it, staticValue: swapText(it.staticValue) })) };
      case 'signature':
        return {
          ...sec,
          parties: sec.parties.map((p) => ({
            ...p,
            rows: p.rows.map((r) => (matchesFrom(r.fieldId) ? { ...r, fieldId: toId } : { ...r, staticValue: swapText(r.staticValue) })),
          })),
        };
      case 'attachment':
      case 'code':
      case 'rich_text':
        return matchesFrom(sec.fieldId) ? ({ ...sec, fieldId: toId } as ContractSection) : sec;
      default:
        return sec;
    }
  });
}

/**
 * 画布上改完标题 / 文本段落的文字。
 * 整块被一个 span 包住带着字号 / 粗细 —— 用户在气泡里对全部文字按了 A+ 或加粗 ——
 * 这是区块级的意图，提升成 fontSizePx / bold（属性栏里也能看到），不留在内联里；
 * 留在内联里的话之后改属性栏字号对它不生效。
 */
export function applySectionText(sections: ContractSection[], id: string, text: string): ContractSection[] {
  return mapSectionsDeep(sections, (sec) => {
    if (sec.id !== id || (sec.type !== 'header' && sec.type !== 'text_block')) return sec;
    const ex = extractWholeWrapStyles(text);
    const next = { ...sec, content: ex.content } as ContractSection & { fontSizePx?: number | null; bold?: boolean };
    if (ex.fontSizePx) next.fontSizePx = ex.fontSizePx;
    if (sec.type === 'header' && typeof ex.bold === 'boolean') next.bold = ex.bold;
    return next;
  });
}

/** 改一个表格格子的文字（画布上双击就地编辑）。 */
export function applyCellText(
  sections: ContractSection[],
  id: string,
  r: number,
  c: number,
  text: string,
): ContractSection[] {
  return mapSectionsDeep(sections, (sec) => {
    if (sec.id !== id || sec.type !== 'free_table') return sec;
    return patchCells(sec as FreeTableSection, { r0: r, c0: c, r1: r, c1: c }, { content: text });
  });
}

/**
 * 分栏：真正的容器，每栏里能再放任何区块（表格、图片、签章都行）。
 *
 * 第一版是拿无框表格冒充的 —— 栏里只能放文字，用户想在右栏放一张表格就
 * 卡住了，这正是竞品对比里那条「分栏栏内嵌块」。老模板里的无框表格照常
 * 是表格，不迁移。
 */
export function createColumnsSection(cols = 2): ColumnsSection {
  const n = Math.max(2, Math.min(4, cols));
  const width = Math.round((100 / n) * 10) / 10;
  const widths = Array.from({ length: n }, (_, i) =>
    i === n - 1 ? Math.round((100 - width * (n - 1)) * 10) / 10 : width,
  );
  return { type: 'columns', id: genId(), widths, columns: Array.from({ length: n }, () => []), gapMm: 4 };
}

/** 斜杠菜单选了某个块类型 → 造出这个区块。'columns' 是分栏预设。 */
export function createBlock(type: string): ContractSection | null {
  if (type === 'columns') return createColumnsSection(2);
  try {
    return createDefaultSection(type as ContractSectionType);
  } catch {
    return null;
  }
}

/** 在指定区块后面插入一个新区块（它在哪一层就插在哪一层），返回新数组和新区块 id。 */
export function insertAfter(
  sections: ContractSection[],
  afterId: string,
  block: ContractSection,
): { sections: ContractSection[]; id: string } {
  return { sections: insertAfterDeep(sections, afterId, block), id: block.id };
}

/** 区块所在的那一层是否允许上移/下移（给面板按钮置灰用）。 */
export function canMove(sections: ContractSection[], id: string, dir: -1 | 1): boolean {
  const hit = locateSection(sections, id);
  if (!hit) return false;
  const to = hit.index + dir;
  return to >= 0 && to < hit.list.length;
}
